/**
 * "Add a setting" — the form that writes one field descriptor into a design.
 *
 * A design is otherwise authored two ways: generated wholesale from the
 * snapshot, or hand-edited on disk. Neither helps someone who wants to add the
 * one path the generator skipped, or re-add a row they pruned — so this is the
 * third way, and it asks for exactly what `addField` accepts.
 *
 * IT SUGGESTS, IT DOES NOT DECIDE. The path list comes from the snapshot's own
 * variables (`sniffableFieldPaths`, the same rule the generator applies) and
 * picking one prefills the type and label from what the value actually is. Both
 * stay editable, and a path the snapshot does not have yet can be typed in
 * freehand — the panel already has a row state for a design that is ahead of its
 * snapshot, so declaring one early is legitimate.
 *
 * NOTHING IS PERSISTED HERE. `onSubmit` hands the caller a spec and a group id;
 * the panel owns the write and the rollback, like every other design edit.
 *
 * CHROME. Panel-coloured paper and small-caps sections, matching the snapshot
 * editor's export and delivery dialogs — this one opens over the same surface
 * they do, and a form of ten controls needs the grouping more than they do.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  DESIGN_FIELD_TYPES,
  assetFieldPath,
  assetKeyFromPath,
  humanizeKey,
  labelForPath,
  sniffableFieldPaths,
} from "../../modules/playEngineDesign";
import { describeAssetSize } from "./designAssetSource";
import { BAR_BG, FIELD_LABEL, PANEL_BG } from "./designFields";

/** Offered in the order an author is likely to want them, not alphabetically. */
const TYPE_ORDER = ["number", "integer", "string", "boolean", "color", "vec2", "array", "object", "asset"];
const TYPES = TYPE_ORDER.filter((type) => DESIGN_FIELD_TYPES.has(type));

const BORDER = "1px solid rgba(255,255,255,0.09)";

const useStyles = makeStyles({
  // Sections, each introduced by a small-caps label — the shape the export and
  // delivery dialogs use. Ten controls in one column is a wall; three named
  // groups of three or four is a form.
  section: { marginTop: "20px" },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "8px",
  },
  sectionTitle: {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: FIELD_LABEL,
  },
  sectionNote: { fontSize: "11px", color: "#8a8d93" },

  row: { display: "flex", gap: "10px", alignItems: "flex-start" },
  rowSpaced: { display: "flex", gap: "10px", alignItems: "flex-start", marginTop: "12px" },
  // min / max / step sit together and are two or three characters wide; giving
  // them equal thirds of the row wastes the space the control select needs.
  narrow: { flex: "0 0 84px" },
  grow: { flex: "1 1 0", minWidth: 0 },

  // What the snapshot holds at the chosen path, so "is this the right one?" is
  // answered here rather than by adding the row and looking.
  value: {
    marginTop: "10px",
    padding: "8px 10px",
    borderRadius: "6px",
    backgroundColor: BAR_BG,
    border: BORDER,
    fontSize: "11px",
    fontFamily: "monospace",
    color: "#c9ccd2",
    wordBreak: "break-all",
    maxHeight: "72px",
    overflow: "auto",
  },
  valueLabel: { color: "#8a8d93", marginRight: "6px" },

  checks: { display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "4px" },
  check: { marginLeft: 0 },
  checkLabel: { fontSize: "12px" },

  optionRow: { fontSize: "12px", fontFamily: "monospace", minWidth: 0 },
  optionType: { fontSize: "10px", color: "#8a8d93", flexShrink: 0, marginLeft: "8px" },

  footer: {
    marginTop: "20px",
    paddingTop: "10px",
    borderTop: BORDER,
    fontSize: "11px",
    color: "#8a8d93",
    lineHeight: 1.5,
  },
});

/** "" → null, so a blank box means "not decided" rather than zero. */
function toNumberOrNull(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return _.isFinite(parsed) ? parsed : null;
}

/**
 * One option per line: `value`, or `value = Label`.
 *
 * A line-per-option rather than a repeating pair of inputs because the lists
 * these fields carry are short and typed once — three difficulties, four themes
 * — and a textarea is faster than four clicks per row.
 */
/** The textarea lines an options list was typed as. */
function optionLinesOf(options) {
  return (Array.isArray(options) ? options : [])
    .filter((option) => _.isPlainObject(option) && option.value !== undefined)
    .map((option) =>
      option.label === undefined || String(option.label) === String(option.value)
        ? String(option.value)
        : `${option.value} = ${option.label}`
    )
    .join("\n");
}

/**
 * The form, seeded from a descriptor as the design file holds it.
 *
 * The two spellings of a responsive type are folded the way `normalizeField`
 * folds them, so an edit of a hand-written `responsive-number` writes back the
 * form the rest of this dialog produces.
 */
export function formFromField(field, groupId) {
  const rawType = typeof field.type === "string" ? field.type.trim() : "";
  const responsivePrefix = rawType.startsWith("responsive-");
  const type = responsivePrefix ? rawType.slice("responsive-".length) : rawType;

  return {
    path: type === "asset" ? assetKeyFromPath(field.path) || field.path : field.path || "",
    groupId: groupId || "",
    label: typeof field.label === "string" ? field.label : "",
    type: DESIGN_FIELD_TYPES.has(type) ? type : type === "json" ? "object" : "string",
    responsive: responsivePrefix || field.responsive === true,
    control: field.control === "slider" ? "slider" : "scrub",
    min: _.isFinite(field.min) ? String(field.min) : "",
    max: _.isFinite(field.max) ? String(field.max) : "",
    step: _.isFinite(field.step) ? String(field.step) : "",
    options: optionLinesOf(field.options),
    help: typeof field.help === "string" ? field.help : "",
    localizable: field.localizable === true,
    readOnly: field.readOnly === true,
  };
}

export function parseOptionLines(raw) {
  return String(raw ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const at = line.indexOf("=");
      if (at === -1) return { value: line, label: line };
      const value = line.slice(0, at).trim();
      const label = line.slice(at + 1).trim();
      return { value, label: label || value };
    })
    .filter((option) => option.value !== "");
}

/** Fields the form should show for a type — the rest would promise nothing. */
function supports(type) {
  return {
    bounds: type === "number" || type === "integer",
    options: type === "string" || type === "number" || type === "integer",
    localizable: type === "string",
    // An asset is a slot, not a value: there is no orientation pair to split, no
    // enum to constrain it to, and no translation of a picture.
    responsive: type !== "asset",
  };
}

/** The snapshot's value at a path, short enough to sit on two lines. */
function previewValue(value) {
  let text;
  try {
    text = JSON.stringify(value);
  } catch (_err) {
    // A cyclic value is not something a design field describes, but it must not
    // take the dialog down either.
    return String(value);
  }
  if (text === undefined) return String(value);
  return text.length > 240 ? `${text.slice(0, 240)}…` : text;
}

const EMPTY_FORM = {
  path: "",
  groupId: "",
  label: "",
  type: "number",
  responsive: false,
  control: "scrub",
  min: "",
  max: "",
  step: "",
  options: "",
  help: "",
  localizable: false,
  readOnly: false,
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {Array} props.groupEntries   walkGroups output — every group, with depth
 * @param {string|null} props.defaultGroupId  the group the panel has open
 * @param {object} props.variables     the snapshot's globalVariables
 * @param {object} [props.assets]      the snapshot's asset map, for asset rows
 * @param {(path: string) => boolean} props.isPathTaken
 * @param {object} [props.editingField]   the RAW descriptor being edited, if any
 * @param {string} [props.editingGroupId] the group it currently sits in
 * @param {() => void} props.onClose
 * @param {(spec: object, groupId: string) => void} props.onSubmit
 */
export default function DialogAddDesignField({
  open,
  groupEntries,
  defaultGroupId,
  variables,
  assets = {},
  isPathTaken,
  editingField = null,
  editingGroupId = null,
  onClose,
  onSubmit,
}) {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY_FORM);
  // True once the author types a label of their own, after which the path stops
  // rewriting it — otherwise correcting the path would undo their wording.
  const [labelTouched, setLabelTouched] = useState(false);

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  // Reopening is a fresh form, seeded with the group that is on screen: the
  // overwhelmingly common intent is "add a setting to the group I am looking at".
  // Editing seeds it with the descriptor as the FILE holds it, so what is on
  // screen is what would be written back unchanged.
  useEffect(() => {
    if (!open) return;
    setForm(
      editingField
        ? formFromField(editingField, editingGroupId || defaultGroupId || "")
        : { ...EMPTY_FORM, groupId: defaultGroupId || "" }
    );
    setLabelTouched(Boolean(editingField));
  }, [open, defaultGroupId, editingField, editingGroupId]);

  const isEditing = Boolean(editingField);

  const isAssetForm = form.type === "asset";
  // An asset row is keyed by an asset slot and a value row by a globalVariables
  // path, so an edit — which keeps the path — cannot cross between them. The
  // choice is removed rather than refused on save.
  const offeredTypes = isEditing
    ? TYPES.filter((type) => (type === "asset") === (editingField.type === "asset"))
    : TYPES;

  // What the snapshot actually holds, minus what the design already describes.
  const candidates = useMemo(() => {
    if (!open) return [];
    return sniffableFieldPaths(variables).filter((row) => !isPathTaken(row.path));
  }, [open, variables, isPathTaken]);

  // The asset slots this design does not describe yet. Keys, not paths: the
  // sentinel is added on submit so the author never types punctuation.
  const assetCandidates = useMemo(() => {
    if (!open) return [];
    return Object.keys(assets || {})
      .filter((assetKey) => !isPathTaken(assetFieldPath(assetKey)))
      .sort((a, b) => a.localeCompare(b))
      .map((assetKey) => ({ path: assetKey, type: _.get(assets, [assetKey, "type"], "asset") }));
  }, [open, assets, isPathTaken]);

  const activeCandidates = isAssetForm ? assetCandidates : candidates;

  const candidateByPath = useMemo(
    () => new Map(activeCandidates.map((row) => [row.path, row])),
    [activeCandidates]
  );

  const selectedGroup = groupEntries.find((entry) => entry.group.id === form.groupId) || null;
  const shows = supports(form.type);

  /** Adopt what the value IS, for a path the snapshot has. */
  function choosePath(nextPath) {
    const path = String(nextPath ?? "").trim();
    setForm((current) => {
      const next = { ...current, path };
      const known = current.type === "asset" ? null : candidateByPath.get(path);
      if (known) {
        let type = known.type;
        if (type.startsWith("responsive-")) {
          next.responsive = true;
          type = type.slice("responsive-".length);
        } else {
          next.responsive = false;
        }
        if (DESIGN_FIELD_TYPES.has(type)) next.type = type;
      }
      if (!labelTouched && path) {
        next.label =
          current.type === "asset"
            ? humanizeKey(path)
            : labelForPath(path, _.get(selectedGroup, "group.path") || "");
      }
      return next;
    });
  }

  const trimmedPath = form.path.trim();
  // What the design will actually store: an asset row is keyed by its sentinel
  // path, which is also what the duplicate check has to ask about.
  const submitPath = isAssetForm ? assetFieldPath(trimmedPath) : trimmedPath;
  const duplicate = Boolean(trimmedPath) && !isEditing && isPathTaken(submitPath);
  const missingGroup = !form.groupId;
  const canSubmit = Boolean(trimmedPath) && !duplicate && !missingGroup;

  // Said out loud rather than left to a disabled control: a slider with no
  // bounds is the one choice here that silently becomes something else.
  const sliderNeedsBounds =
    form.control === "slider" &&
    shows.bounds &&
    (toNumberOrNull(form.min) === null || toNumberOrNull(form.max) === null);

  const assetEntry = isAssetForm ? _.get(assets, trimmedPath, null) : null;
  const hasValue = isAssetForm
    ? Boolean(assetEntry)
    : Boolean(trimmedPath) && _.has(variables, trimmedPath);
  const snapshotValue = hasValue && !isAssetForm ? _.get(variables, trimmedPath) : undefined;

  function handleSubmit() {
    if (!canSubmit) return;
    const options = shows.options ? parseOptionLines(form.options) : [];
    onSubmit(
      {
        path: submitPath,
        type: form.type,
        responsive: shows.responsive && form.responsive,
        label: form.label,
        help: form.help,
        control: shows.bounds ? form.control : "scrub",
        min: shows.bounds ? toNumberOrNull(form.min) : null,
        max: shows.bounds ? toNumberOrNull(form.max) : null,
        step: shows.bounds ? toNumberOrNull(form.step) : null,
        options,
        localizable: shows.localizable && form.localizable,
        readOnly: form.readOnly,
      },
      form.groupId
    );
  }

  function section(title, note, children) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionHead}>
          <span className={classes.sectionTitle}>{title}</span>
          {note ? <span className={classes.sectionNote}>{note}</span> : null}
        </div>
        {children}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle>{isEditing ? "Edit setting" : "Add a setting"}</DialogTitle>
      <DialogContent>
        {section(
          isAssetForm ? "The asset" : "The value",
          activeCandidates.length ? `${activeCandidates.length} not yet described` : null,
          <>
            <Autocomplete
              freeSolo
              openOnFocus
              disabled={isEditing}
              options={activeCandidates.map((candidate) => candidate.path)}
              value={form.path}
              inputValue={form.path}
              onChange={(event, value) => choosePath(value)}
              onInputChange={(event, value, reason) => {
                if (reason !== "reset") choosePath(value);
              }}
              renderOption={(path) => (
                <span style={{ display: "flex", alignItems: "baseline", width: "100%", minWidth: 0 }}>
                  <span className={classes.optionRow}>{path}</span>
                  <span style={{ flex: 1 }} />
                  <span className={classes.optionType}>{candidateByPath.get(path)?.type}</span>
                </span>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={!isEditing}
                  label={isAssetForm ? "Asset key" : "Variable path"}
                  size="small"
                  fullWidth
                  error={duplicate}
                  helperText={
                    duplicate
                      ? isAssetForm
                        ? "This design already has a row for that asset."
                        : "This design already has a row for that path."
                      : isEditing
                        ? "The value this row describes. Point a row at another value by removing it and adding the other one."
                        : isAssetForm
                          ? "The key this asset has in the Assets tab, e.g. bgImg"
                          : "Where in globalVariables the value lives, e.g. tuning.hint.delay"
                  }
                  onKeyDown={(event) => event.stopPropagation()}
                />
              )}
            />

            {/* Either what is there, or a plain statement that nothing is — the
                panel renders such a row as "not in this snapshot", so declaring
                one early is allowed and just has to be said. */}
            {trimmedPath ? (
              <div className={classes.value}>
                <span className={classes.valueLabel}>{hasValue ? "now" : "—"}</span>
                {isAssetForm
                  ? hasValue
                    ? `${_.get(assetEntry, "type", "asset")} · ${describeAssetSize(assetEntry)}`
                    : "no asset with that key in this snapshot yet"
                  : hasValue
                    ? previewValue(snapshotValue)
                    : "not in this snapshot yet"}
              </div>
            ) : null}

            <div className={classes.rowSpaced}>
              <FormControl size="small" className={classes.grow}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    // Crossing between a value and an asset changes what the
                    // box above is asking for, so the half-typed answer to the
                    // other question does not survive the switch.
                    setForm((current) => {
                      const crossing =
                        !isEditing && (current.type === "asset") !== (nextType === "asset");
                      if (!crossing) return { ...current, type: nextType };
                      return { ...current, type: nextType, path: "", label: "" };
                    });
                    // `nextType`, not the event: by now the state update above
                    // may have flushed and React will have released it.
                    if (form.type === "asset" || nextType === "asset") setLabelTouched(false);
                  }}
                >
                  {offeredTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {shows.responsive && (
                <FormControlLabel
                  className={classes.check}
                  style={{ flexShrink: 0, marginTop: 4 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.responsive}
                      onChange={(event) => set("responsive")(event.target.checked)}
                    />
                  }
                  label={<span className={classes.checkLabel}>Portrait / landscape pair</span>}
                />
              )}
            </div>
          </>
        )}

        {section(
          "The row",
          null,
          <>
            <div className={classes.row}>
              <TextField
                label="Label"
                size="small"
                className={classes.grow}
                value={form.label}
                placeholder={trimmedPath ? labelForPath(trimmedPath, "") : ""}
                helperText="Left blank, it is made from the path."
                onChange={(event) => {
                  setLabelTouched(true);
                  set("label")(event.target.value);
                }}
                onKeyDown={(event) => event.stopPropagation()}
              />
              <TextField
                label="Help"
                size="small"
                className={classes.grow}
                value={form.help}
                helperText="Tooltip beside the label."
                onChange={(event) => set("help")(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            </div>

            {shows.bounds && (
              <div className={classes.rowSpaced}>
                <FormControl size="small" className={classes.grow} error={sliderNeedsBounds}>
                  <InputLabel>Control</InputLabel>
                  <Select
                    value={form.control}
                    onChange={(event) => set("control")(event.target.value)}
                  >
                    <MenuItem value="scrub">Scrub field</MenuItem>
                    <MenuItem value="slider">Slider</MenuItem>
                  </Select>
                </FormControl>
                {["min", "max", "step"].map((key) => (
                  <TextField
                    key={key}
                    label={key}
                    size="small"
                    className={classes.narrow}
                    value={form[key]}
                    error={sliderNeedsBounds && key !== "step"}
                    onChange={(event) => set(key)(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                ))}
              </div>
            )}

            {sliderNeedsBounds && (
              <div className={classes.sectionNote} style={{ color: "#e0b96a", marginTop: 6 }}>
                A slider needs a min and a max — without them the row is a scrub field.
              </div>
            )}

            {shows.options && (
              <TextField
                label="Choices"
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                style={{ marginTop: 12 }}
                value={form.options}
                placeholder={"easy = Easy\nhard = Hard"}
                helperText="One per line, as `value` or `value = Label`. Any lines here make the row a dropdown."
                onChange={(event) => set("options")(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            )}

            <div className={classes.checks}>
              {shows.localizable && (
                <FormControlLabel
                  className={classes.check}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.localizable}
                      onChange={(event) => set("localizable")(event.target.checked)}
                    />
                  }
                  label={<span className={classes.checkLabel}>Translatable</span>}
                />
              )}
              <FormControlLabel
                className={classes.check}
                control={
                  <Checkbox
                    size="small"
                    checked={form.readOnly}
                    onChange={(event) => set("readOnly")(event.target.checked)}
                  />
                }
                label={<span className={classes.checkLabel}>Read only</span>}
              />
            </div>
          </>
        )}

        {section(
          "Where it goes",
          null,
          <FormControl size="small" fullWidth error={missingGroup}>
            <InputLabel>Group</InputLabel>
            <Select value={form.groupId} onChange={(event) => set("groupId")(event.target.value)}>
              {groupEntries.map((entry) => (
                <MenuItem key={entry.group.id} value={entry.group.id}>
                  {/* Indented so a nested group reads as nested — the outliner's
                      shape, flattened into a list. */}
                  <span style={{ paddingLeft: entry.depth * 14 }}>{entry.group.label}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <div className={classes.footer}>
          This describes a value; it does not create one. Nothing in the snapshot changes until the
          row is edited, and removing the row later leaves the value where it is.
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={!canSubmit} onClick={handleSubmit}>
          {isEditing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
